import { toPercentNumber } from './toPercentNumber.ts';

type ToPercentStringOptions = {
  /**
   * The number of decimal places to include in the formatted percentage string.
   *
   * @defaultValue 1
   */
  decimalPlaces?: number;
  /**
   * The power of 10 by which to scale the input value before formatting.
   *
   * @defaultValue -2
   * @example
   * // -2 -> multiplies by 100 (10^2)
   * // -3 -> multiplies by 1000 (10^3)
   */
  scaleFactor?: number;
};

const DEFAULT_OPTIONS = {
  decimalPlaces: 1,
  scaleFactor: -2,
} satisfies ToPercentStringOptions;

/**
 * Converts an input value to a formatted percentage string.
 *
 * @example
 * ```ts
 * toPercentString(0.125); // returns '12.5%'
 * toPercentString('0.125', { decimalPlaces: 2 }); // returns '12.50%'
 * toPercentString(12.5, { scaleFactor: 0 }); // returns '12.5%'
 * toPercentString(null); // returns undefined
 * ```
 *
 * @param value - The value to convert and format.
 * @param options - Optional configuration object {@link ToPercentStringOptions}.
 *
 * @returns The formatted percentage string, or `undefined` if the conversion fails.
 */
export const toPercentString = (value: null | number | string | undefined, options?: ToPercentStringOptions) => {
  const decimalPlaces = options?.decimalPlaces ?? DEFAULT_OPTIONS.decimalPlaces;
  const scaleFactor = options?.scaleFactor ?? DEFAULT_OPTIONS.scaleFactor;

  const percent = toPercentNumber(value, { fallback: NaN, scaleFactor });

  return Number.isNaN(percent) ? undefined : `${percent.toFixed(decimalPlaces)}%`;
};
